import { useState, useCallback, useMemo } from 'react';

/** Run ``fn`` up to ``retries + 1`` times with linear backoff (for cold starts / flaky API). */
export async function fetchWithRetry<T>(
  fn: () => Promise<T>,
  options?: { retries?: number; baseDelayMs?: number },
): Promise<T> {
  const retries = options?.retries ?? 2;
  const baseDelayMs = options?.baseDelayMs ?? 400;
  let last: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (e) {
      last = e;
      if (attempt < retries) {
        await new Promise((r) => setTimeout(r, baseDelayMs * (attempt + 1)));
      }
    }
  }
  throw last;
}

interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiry: number;
}

interface UseCacheOptions {
  cacheKey: string;
  cacheDuration?: number; // in milliseconds, default 5 minutes
}

// Pure localStorage read. Hoisted out of the hook so it can be used as a
// lazy useState initializer and inside the prev-key reset below without
// depending on React state.
function readFromCache<T>(cacheKey: string): T | null {
  try {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const cacheItem: CacheItem<T> = JSON.parse(cached);
    if (Date.now() > cacheItem.expiry) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    return cacheItem.data;
  } catch (error) {
    console.error('Error reading from cache:', error);
    localStorage.removeItem(cacheKey);
    return null;
  }
}

export function useCache<T>(options: UseCacheOptions) {
  const { cacheKey, cacheDuration = 5 * 60 * 1000 } = options; // 5 minutes default

  // Seed state directly from localStorage on first render instead of
  // sync-ing via useEffect+setState (which trips
  // react-hooks/set-state-in-effect in React 19).
  const [cachedData, setCachedData] = useState<T | null>(() => readFromCache<T>(cacheKey));
  const [isLoading, setIsLoading] = useState(false);

  // When cacheKey changes (e.g. useInstancesCache switches guildId), re-read
  // from localStorage during render. Pattern is explicitly endorsed by React
  // docs for "storing information from previous renders":
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  // useState (not useRef) is required here because the new react-hooks/refs
  // lint forbids reading/writing refs during render.
  const [prevCacheKey, setPrevCacheKey] = useState(cacheKey);
  if (prevCacheKey !== cacheKey) {
    setPrevCacheKey(cacheKey);
    setCachedData(readFromCache<T>(cacheKey));
  }

  const getCachedData = useCallback(
    (): T | null => readFromCache<T>(cacheKey),
    [cacheKey],
  );

  // Set data in cache
  const setCacheData = useCallback((data: T) => {
    try {
      const cacheItem: CacheItem<T> = {
        data,
        timestamp: Date.now(),
        expiry: Date.now() + cacheDuration,
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheItem));
      setCachedData(data);
    } catch (error) {
      console.error('Error writing to cache:', error);
    }
  }, [cacheKey, cacheDuration]);

  // Clear cache
  const clearCache = useCallback(() => {
    localStorage.removeItem(cacheKey);
    setCachedData(null);
  }, [cacheKey]);

  // Fetch data with caching
  const fetchWithCache = useCallback(async <R>(
    fetchFn: () => Promise<R>,
    options?: { forceRefresh?: boolean }
  ): Promise<R> => {
    const { forceRefresh = false } = options || {};

    // Check cache first unless force refresh
    if (!forceRefresh) {
      const cached = getCachedData() as R;
      if (cached) {
        return cached;
      }
    }

    setIsLoading(true);
    try {
      const data = await fetchFn();
      setCacheData(data as T);
      return data;
    } finally {
      setIsLoading(false);
    }
  }, [getCachedData, setCacheData]);

  return useMemo(
    () => ({
      cachedData,
      isLoading,
      setCacheData,
      clearCache,
      fetchWithCache,
    }),
    [cachedData, isLoading, setCacheData, clearCache, fetchWithCache],
  );
}

// Specific hooks for common cache keys
export function useGuildsCache() {
  return useCache<any[]>({
    cacheKey: 'discord-guilds',
    cacheDuration: 10 * 60 * 1000, // 10 minutes - guilds don't change often
  });
}

export function useInstancesCache(guildId: string) {
  // Don't create cache for empty guild IDs to prevent invalid API calls
  const shouldUseCache = Boolean(guildId && guildId.trim());

  const cache = useCache<any[]>({
    cacheKey: shouldUseCache ? `instances-${guildId}` : 'instances-invalid',
    cacheDuration: 2 * 60 * 1000, // 2 minutes - instances change more frequently
  });

  const disabledStub = useMemo(
    () => ({
      cachedData: null as any[] | null,
      isLoading: false,
      setCacheData: () => {},
      clearCache: () => {},
      fetchWithCache: async () => {
        throw new Error('Cannot fetch instances without a valid guild ID');
      },
    }),
    [],
  );

  if (!shouldUseCache) {
    return disabledStub;
  }

  return cache;
}

export function useChannelsCache(guildId: string) {
  return useCache<any[]>({
    cacheKey: `channels-${guildId}`,
    cacheDuration: 15 * 60 * 1000, // 15 minutes - channels rarely change
  });
}

// Utility functions for cache management
export function clearInstancesCache(guildId: string) {
  // Don't try to clear cache for empty guild IDs
  if (!guildId || !guildId.trim()) {
    return;
  }
  localStorage.removeItem(`instances-${guildId}`);
}

export function clearAllInstancesCaches() {
  const keys = Object.keys(localStorage);
  keys.forEach(key => {
    if (key.startsWith('instances-')) {
      localStorage.removeItem(key);
    }
  });
}

export function clearGuildsCache() {
  localStorage.removeItem('discord-guilds');
}

export function clearChannelsCache(guildId: string) {
  localStorage.removeItem(`channels-${guildId}`);
} 